import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { FiSearch, FiX, FiPackage, FiSliders } from "react-icons/fi";

import MainContainer from "../../../components/utils/MainContainer";
import Seo from "../../../components/utils/Seo";
import ProductCard from "../../../components/portal/ProductCard";
import CustomSelect from "../../../components/utils/CustomSelect";
import { ProductGridSkeleton } from "../../../components/utils/Skeleton";
import { useScrollLock } from "../../../hooks/useScrollLock";
import useReveal from "../../../hooks/useReveal";

import productsService from "../../../api/services/products";
import { selectCategories } from "../../../store/slices/products/slice";
import { selectBandsDropdown } from "../../../store/slices/bands/slice";
import { slugify } from "../../../utils/utils";

import {
  Page,
  HeaderRow,
  SectionLabel,
  TitleRow,
  PageH1,
  ResultCount,
  Toolbar,
  ToolbarInner,
  ChipsRail,
  CategoryChip,
  ToolbarControls,
  SearchWrap,
  SearchField,
  ActiveFilters,
  ActiveFilterChip,
  ClearAllButton,
  Grid,
  RevealCell,
  LoadMoreRow,
  GhostButton,
  PageLinks,
  EmptyState,
  FiltersButton,
  SheetBackdrop,
  Sheet,
  SheetHandle,
  SheetTitle,
  SheetField,
  SheetApply,
} from "./ShopPageNew.styles";

const PAGE_SIZE = 12; // koliko backend vraća po strani (fiksno)
const DISPLAY_PAGE_SIZE = 20; // koliko se prikazuje po "strani" u gridu
const MAX_FETCH_PAGES = 30; // sigurnosni limit (backend: 12 po strani)
const FESTIVAL_PREFIX = "festival:"; // prefiks vrednosti u select-u za vlasnika

const SORT_OPTIONS = [
  // vrednost ostaje "najnovije" da postojeći URL-ovi ne umru
  { value: "najnovije", label: "Preporučeno" },
  { value: "cena-asc", label: "Cena ↑" },
  { value: "cena-desc", label: "Cena ↓" },
  { value: "naziv", label: "Naziv" },
];

// Backend vraća proizvode grupisane po kategorijama, spljošti u jedan niz
const flattenProducts = (data) => {
  if (!Array.isArray(data)) return [];
  if (data[0]?.categoryName && data[0]?.products) {
    return data.flatMap((category) =>
      Array.isArray(category.products) ? category.products : [],
    );
  }
  return data;
};

const effectivePrice = (product) =>
  Number(product.salePrice || product.price) || 0;

/* Grid ćelija sa sopstvenim scroll-reveal observerom; --i je kolona u redu,
   pa red ulazi kaskadno sleva nadesno */
const RevealGridCell = ({ columnIndex, children }) => {
  const [ref, visible] = useReveal({ rootMargin: "0px 0px -40px 0px" });
  return (
    <RevealCell ref={ref} $visible={visible} style={{ "--i": columnIndex }}>
      {children}
    </RevealCell>
  );
};

export default function ShopPageNew() {
  const [searchParams, setSearchParams] = useSearchParams();

  const categories = useSelector(selectCategories);
  const { data: bandsData } = useSelector(selectBandsDropdown);

  /* ------------------- katalog: sve odjednom, jednom ------------------- */
  // Backend fiksira 12 po strani, povuci prvu stranu pa ostatak paralelno.
  // Katalog je mali (~100), pa filteri i sortiranje rade klijentski, egzaktno.
  const [allProducts, setAllProducts] = useState(null); // null = učitava se
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let active = true;

    const loadAll = async () => {
      try {
        const first = await productsService.getProducts({ page: 1 });
        const firstItems = flattenProducts(first?.data);
        const perPage = Number(first?.perPage) || PAGE_SIZE;
        const total = Number(first?.total) || firstItems.length;
        const totalPages = Math.min(
          Math.ceil(total / perPage) || 1,
          MAX_FETCH_PAGES,
        );

        let restItems = [];
        if (totalPages > 1) {
          const responses = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              productsService.getProducts({ page: i + 2 }),
            ),
          );
          restItems = responses.flatMap((response) =>
            flattenProducts(response?.data),
          );
        }

        if (!active) return;
        const seen = new Set();
        const merged = [...firstItems, ...restItems].filter((product) => {
          if (!product?.id || seen.has(product.id)) return false;
          seen.add(product.id);
          return true;
        });
        setAllProducts(merged);
      } catch (error) {
        if (!active) return;
        setLoadError(error.message);
        setAllProducts([]);
      }
    };

    loadAll();
    return () => {
      active = false;
    };
  }, []);

  /* ----------------------- filteri iz URL-a ----------------------- */
  // Novi parametri: kat/bend/sort/q/page. Legacy (kategorija=id, search=)
  // se i dalje čitaju da stari linkovi ne umru.
  const katParam = searchParams.get("kat") || searchParams.get("kategorija") || "";
  const bendParam = searchParams.get("bend") || "";
  const festivalParam = searchParams.get("festival") || "";
  const sort = searchParams.get("sort") || "najnovije";
  const q = searchParams.get("q") ?? searchParams.get("search") ?? "";
  const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);

  const activeCategories = useMemo(
    () => (categories?.data || []).filter((c) => c.active !== false),
    [categories?.data],
  );

  const selectedCategory = useMemo(
    () =>
      activeCategories.find(
        (c) => slugify(c.name) === katParam || String(c.id) === katParam,
      ) || null,
    [activeCategories, katParam],
  );

  const selectedBand = useMemo(
    () =>
      (bandsData || []).find(
        (b) => slugify(b.name) === bendParam || String(b.id) === bendParam,
      ) || null,
    [bandsData, bendParam],
  );

  // Festivali koji stvarno imaju proizvode u katalogu — izvedeno iz samih
  // proizvoda, pa u filteru nikad ne stoji prazan festival
  const festivalOwners = useMemo(() => {
    const byId = new Map();
    (allProducts || []).forEach((product) => {
      const festival = product.festival;
      if (festival?.id && !byId.has(String(festival.id))) {
        byId.set(String(festival.id), festival);
      }
    });
    return [...byId.values()].sort((a, b) =>
      String(a.name).localeCompare(String(b.name), "sr"),
    );
  }, [allProducts]);

  const selectedFestival = useMemo(
    () =>
      festivalOwners.find((f) => String(f.id) === String(festivalParam)) ||
      null,
    [festivalOwners, festivalParam],
  );

  // Jedan select za vlasnika: bendovi drže postojeći ?bend=slug,
  // festivali idu kao ?festival=id (prefiks samo u vrednosti opcije)
  const ownerOptions = useMemo(
    () => [
      { value: "", label: "Svi bendovi i festivali" },
      ...(bandsData || []).map((band) => ({
        value: slugify(band.name),
        label: band.name,
      })),
      ...festivalOwners.map((festival) => ({
        value: `${FESTIVAL_PREFIX}${festival.id}`,
        label: `${festival.name} (festival)`,
      })),
    ],
    [bandsData, festivalOwners],
  );

  const ownerValue = selectedBand
    ? slugify(selectedBand.name)
    : festivalParam
      ? `${FESTIVAL_PREFIX}${festivalParam}`
      : "";

  const applyParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    // legacy ključeve prepiši u nove pri prvoj interakciji
    next.delete("kategorija");
    next.delete("search");

    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });

    // promena filtera resetuje stranicu
    if (!("page" in patch)) next.delete("page");
    if (next.get("page") === "1") next.delete("page");
    if (next.get("sort") === "najnovije") next.delete("sort");

    setSearchParams(next, { replace: true });
  };

  const handleOwnerChange = (value) => {
    if (!value) return applyParams({ bend: null, festival: null });
    if (value.startsWith(FESTIVAL_PREFIX)) {
      return applyParams({
        bend: null,
        festival: value.slice(FESTIVAL_PREFIX.length),
      });
    }
    return applyParams({ bend: value, festival: null });
  };

  const clearAllFilters = () => setSearchParams({}, { replace: true });

  /* ------------------- mobilni filteri (bottom-sheet) ------------------- */
  const [sheetOpen, setSheetOpen] = useState(false);
  useScrollLock(sheetOpen);

  const mobileFilterCount =
    (selectedBand || festivalParam ? 1 : 0) +
    (sort !== "najnovije" ? 1 : 0) +
    (q.trim() ? 1 : 0);

  /* ----------------------- search (debounce) ----------------------- */
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    setSearchInput(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== q) {
        applyParams({ q: searchInput.trim() || null });
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  /* ------------------- filtriranje + sortiranje ------------------- */
  const filtered = useMemo(() => {
    if (!allProducts) return [];
    const query = q.trim().toLowerCase();

    return allProducts.filter((product) => {
      if (selectedCategory) {
        const categoryId = product.categoryId || product.category?.id;
        if (String(categoryId) !== String(selectedCategory.id)) return false;
      }
      if (selectedBand) {
        const bandId = product.bandId || product.band?.id;
        if (String(bandId) !== String(selectedBand.id)) return false;
      }
      if (festivalParam) {
        const festivalId = product.festivalId || product.festival?.id;
        if (String(festivalId) !== String(festivalParam)) return false;
      }
      if (query) {
        const owner = product.band?.name || product.festival?.name || "";
        const haystack = `${product.name} ${owner}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [allProducts, selectedCategory, selectedBand, festivalParam, q]);

  // Kategorije "majica" — u default redosledu idu pre ostalih kategorija
  const majiceCategoryIds = useMemo(
    () =>
      new Set(
        (categories?.data || [])
          .filter((c) => (c.name || "").toLowerCase().includes("majic"))
          .map((c) => String(c.id)),
      ),
    [categories?.data],
  );

  const sorted = useMemo(() => {
    const list = [...filtered];

    // Vlasnik sa manjim listPriority ide prvi, bez broja na kraj. Bendovi i
    // festivali dele istu skalu, pa festivalski merch može i na prvo mesto.
    const ownerRank = (product) => {
      const priority =
        product.band?.listPriority ?? product.festival?.listPriority;
      return priority != null ? priority : Number.MAX_SAFE_INTEGER;
    };
    const categoryRank = (product) =>
      majiceCategoryIds.has(String(product.categoryId || product.category?.id))
        ? 0
        : 1;

    switch (sort) {
      case "cena-asc":
        return list.sort((a, b) => effectivePrice(a) - effectivePrice(b));
      case "cena-desc":
        return list.sort((a, b) => effectivePrice(b) - effectivePrice(a));
      case "naziv":
        return list.sort((a, b) =>
          String(a.name).localeCompare(String(b.name), "sr"),
        );
      default:
        // Preporučeno: prvo SVE majice (po redosledu bendova), zatim ostale
        // kategorije (opet po redosledu bendova), najnovije unutar toga
        return list.sort(
          (a, b) =>
            categoryRank(a) - categoryRank(b) ||
            ownerRank(a) - ownerRank(b) ||
            new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
        );
    }
  }, [filtered, sort, majiceCategoryIds]);

  const visible = sorted.slice(0, page * DISPLAY_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(sorted.length / DISPLAY_PAGE_SIZE));
  const hasMore = visible.length < sorted.length;
  const loading = allProducts === null;
  const hasActiveFilters = Boolean(
    selectedCategory || selectedBand || festivalParam || q.trim(),
  );

  /* -------------------------- URL za crawler fallback -------------------------- */
  const pageHref = (n) => {
    const params = new URLSearchParams(searchParams);
    params.delete("kategorija");
    params.delete("search");
    if (n <= 1) params.delete("page");
    else params.set("page", String(n));
    const qs = params.toString();
    return `/prodavnica${qs ? `?${qs}` : ""}`;
  };

  return (
    <MainContainer>
      <Seo
        title="Prodavnica"
        description="Zvanični merch domaćih bendova i festivala, majice, duksevi i aksesoari. Deo svake kupovine ide direktno bendu."
        canonical="/prodavnica"
      />
      <Page>
        <HeaderRow>
          <SectionLabel>Prodavnica</SectionLabel>
          <TitleRow>
            <PageH1>Prodavnica</PageH1>
            {!loading && (
              <ResultCount>
                {sorted.length}{" "}
                {sorted.length === 1 ? "proizvod" : "proizvoda"}
              </ResultCount>
            )}
          </TitleRow>
        </HeaderRow>

        {/* Sticky filter toolbar */}
        <Toolbar>
          <ToolbarInner>
            <ChipsRail>
              <CategoryChip
                type="button"
                $active={!selectedCategory}
                onClick={() => applyParams({ kat: null })}
              >
                Sve
              </CategoryChip>
              {activeCategories.map((category) => (
                <CategoryChip
                  key={category.id}
                  type="button"
                  $active={selectedCategory?.id === category.id}
                  onClick={() => applyParams({ kat: slugify(category.name) })}
                >
                  {category.name}
                </CategoryChip>
              ))}
            </ChipsRail>

            <ToolbarControls>
              <CustomSelect
                value={ownerValue}
                onChange={handleOwnerChange}
                options={ownerOptions}
                placeholder="Bend / festival"
              />
              <CustomSelect
                value={sort}
                onChange={(value) => applyParams({ sort: value })}
                options={SORT_OPTIONS}
                placeholder="Sortiraj"
              />
              <SearchWrap>
                <FiSearch size={15} />
                <SearchField
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Pretraži..."
                  aria-label="Pretraga proizvoda"
                />
              </SearchWrap>
            </ToolbarControls>
          </ToolbarInner>
        </Toolbar>

        {/* Plutajuće "Filteri" dugme (samo mobilno), van Toolbara jer njegov
            backdrop-filter pravi containing block i lomi position: fixed */}
        <FiltersButton type="button" onClick={() => setSheetOpen(true)}>
          <FiSliders size={15} /> Filteri
          {mobileFilterCount > 0 && (
            <span className="count">{mobileFilterCount}</span>
          )}
        </FiltersButton>

        {/* Aktivni filteri */}
        {hasActiveFilters && (
          <ActiveFilters>
            {selectedCategory && (
              <ActiveFilterChip
                type="button"
                onClick={() => applyParams({ kat: null })}
              >
                {selectedCategory.name} <FiX size={13} />
              </ActiveFilterChip>
            )}
            {selectedBand && (
              <ActiveFilterChip
                type="button"
                onClick={() => applyParams({ bend: null })}
              >
                {selectedBand.name} <FiX size={13} />
              </ActiveFilterChip>
            )}
            {festivalParam && (
              <ActiveFilterChip
                type="button"
                onClick={() => applyParams({ festival: null })}
              >
                {selectedFestival?.name || "Festival"} <FiX size={13} />
              </ActiveFilterChip>
            )}
            {q.trim() && (
              <ActiveFilterChip
                type="button"
                onClick={() => {
                  setSearchInput("");
                  applyParams({ q: null });
                }}
              >
                "{q.trim()}" <FiX size={13} />
              </ActiveFilterChip>
            )}
            <ClearAllButton type="button" onClick={clearAllFilters}>
              Poništi sve
            </ClearAllButton>
          </ActiveFilters>
        )}

        {/* Sadržaj */}
        {loading ? (
          <ProductGridSkeleton count={8} />
        ) : sorted.length === 0 ? (
          <EmptyState>
            <FiPackage size={40} />
            <p>
              {loadError
                ? "Greška pri učitavanju proizvoda."
                : "Nema proizvoda za ovaj filter."}
            </p>
            {hasActiveFilters && (
              <GhostButton type="button" onClick={clearAllFilters}>
                Poništi filtere
              </GhostButton>
            )}
          </EmptyState>
        ) : (
          <>
            <Grid>
              {visible.map((product, index) => (
                <RevealGridCell key={product.id} columnIndex={index % 4}>
                  <ProductCard product={product} />
                </RevealGridCell>
              ))}
            </Grid>

            {hasMore && (
              <LoadMoreRow>
                <GhostButton
                  type="button"
                  onClick={() => applyParams({ page: page + 1 })}
                >
                  Učitaj još ({sorted.length - visible.length})
                </GhostButton>
              </LoadMoreRow>
            )}

            {/* Numerisana paginacija, fallback za crawlere */}
            {totalPages > 1 && (
              <PageLinks aria-label="Stranice">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (n) => (
                    <a
                      key={n}
                      href={pageHref(n)}
                      className={n === page ? "active" : ""}
                      onClick={(e) => {
                        e.preventDefault();
                        // namerno bez scroll-to-top, korisnik ostaje gde je bio
                        applyParams({ page: n === 1 ? null : n });
                      }}
                    >
                      {n}
                    </a>
                  ),
                )}
              </PageLinks>
            )}
          </>
        )}
      </Page>

      {/* Mobilni filteri, bottom-sheet */}
      <SheetBackdrop $open={sheetOpen} onClick={() => setSheetOpen(false)} />
      <Sheet $open={sheetOpen}>
        <SheetHandle />
        <SheetTitle>Filteri</SheetTitle>

        <SheetField>
          <label>Bend / festival</label>
          <CustomSelect
            fullWidth
            dropUp
            portal
            value={ownerValue}
            onChange={handleOwnerChange}
            options={ownerOptions}
            placeholder="Bend / festival"
          />
        </SheetField>

        <SheetField>
          <label>Sortiraj</label>
          <CustomSelect
            fullWidth
            dropUp
            portal
            value={sort}
            onChange={(value) => applyParams({ sort: value })}
            options={SORT_OPTIONS}
            placeholder="Sortiraj"
          />
        </SheetField>

        <SheetField>
          <label>Pretraga</label>
          <SearchWrap>
            <FiSearch size={15} />
            <SearchField
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Pretraži..."
              aria-label="Pretraga proizvoda"
            />
          </SearchWrap>
        </SheetField>

        <SheetApply type="button" onClick={() => setSheetOpen(false)}>
          Prikaži rezultate ({sorted.length})
        </SheetApply>
      </Sheet>
    </MainContainer>
  );
}
